import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ProductsService } from '../firebase';
import { Product, weddingDressOptions, sizeOptions } from '../types/product';
import OptimizedImage from '../components/OptimizedImage';

const PageContainer = styled.div`
  padding-top: var(--header-h);
  min-height: 100vh;
  background: white;
  width: 100%;
  overflow-x: hidden;
`;

const Container = styled.div`
  max-width: 100%;
  margin: 0;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 30px;
  padding-top: 20px;

  h1 {
    font-size: 3rem;
    color: #333;
    margin-bottom: 0;
    font-weight: 300;
  }
`;

const HeaderLine = styled.div`
  width: 100%;
  height: 1px;
  background: #ddd;
  margin: 20px 0;
`;

const FilterBox = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0;
  padding: 0;
  border: 1px solid #ddd;
  border-radius: 8px;
  margin-bottom: 30px;
  background: white;
  overflow: visible;
  position: relative;
  z-index: 1001;
`;

const FilterSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 15px 20px;
  border-right: 1px solid #ddd;
  background: white;
  cursor: pointer;
  transition: background-color 0.2s ease;
  min-width: 120px;
  position: relative;
  
  &:last-child {
    border-right: none;
  }
  
  &:hover {
    background: #f8f8f8;
  }
`;

const FilterLabel = styled.span`
  font-size: 14px;
  color: #666;
  font-weight: 500;
`;

const FilterDropdown = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownIcon = styled.span`
  font-size: 12px;
  color: #666;
  transition: transform 0.2s ease;
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 5px);
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 9998;
  min-width: 180px;
  display: ${props => props.$isOpen ? 'block' : 'none'};
`;

const DropdownItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #f8f8f8;
  }
  
  &:first-child {
    border-radius: 6px 6px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 6px 6px;
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 60px;
  justify-items: start;
  width: 100%;
  padding: 0 20px;
  
  @media (max-width: 1400px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const ProductCard = styled.div`
  background: white;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
  min-width: 300px;
  cursor: pointer;
  position: relative;

  &:hover {
    transform: translateY(-5px);
  }
`;

const ProductImageContainer = styled.div`
  height: 600px;
  position: relative;
  overflow: hidden;
  background: #f0f0f0;
`;

const ProductImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const HoverImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.5s ease;
  z-index: 1;
  pointer-events: none;
  
  ${ProductImageContainer}:hover & {
    opacity: 1;
  }
`;

const GradientOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.1));
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 2;
  pointer-events: none;
  
  ${ProductImageContainer}:hover & {
    opacity: 1;
  }
`;

const ProductBadges = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  display: flex;
  gap: 8px;
`;

const Badge = styled.span<{ type: 'sale' | 'featured' | 'new' | 'soldout' }>`
  padding: 7px 14px;
  border-radius: 2px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  line-height: 1;
  backdrop-filter: blur(6px);

  ${props => {
    switch (props.type) {
      case 'sale':
        return 'background: rgba(255, 255, 255, 0.94); color: #b3242f; border: 1px solid rgba(179, 36, 47, 0.35);';
      case 'featured':
        return 'background: #1a1a1a; color: #ffffff; border: 1px solid #1a1a1a;';
      case 'new':
        return 'background: rgba(255, 255, 255, 0.94); color: #1a1a1a; border: 1px solid rgba(26, 26, 26, 0.25);';
      case 'soldout':
        return 'background: rgba(26, 26, 26, 0.82); color: #ffffff; border: 1px solid rgba(26, 26, 26, 0.82);';
      default:
        return 'background: rgba(255, 255, 255, 0.94); color: #1a1a1a; border: 1px solid rgba(26, 26, 26, 0.15);';
    }
  }}
`;

const ProductContent = styled.div`
  padding: 20px;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const ProductName = styled.h3`
  font-family: 'Caviar Dreams', sans-serif;
  font-size: 1.1rem;
  font-weight: bold;
  color: #2c2c2c;
  line-height: 1.3;
  letter-spacing: -0.02em;
  margin: 0;
  text-align: center;
`;

const ProductPrice = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0;
`;

const CurrentPrice = styled.span<{ $onSale?: boolean }>`
  font-size: 0.95rem;
  font-weight: 700;
  color: ${props => props.$onSale ? '#ff4757' : '#333'};
  letter-spacing: -0.02em;
`;


const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);

  h2 {
    font-size: 2rem;
    font-weight: 300;
    color: #333;
    margin-bottom: 20px;
  }

  p {
    font-size: 1.1rem;
    color: #666;
    line-height: 1.6;
    max-width: 500px;
    margin: 0 auto;
  }
`;

const LoadingState = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.95);
  z-index: 9999;

  p {
    font-size: 1.1rem;
    color: #666;
    margin-top: 20px;
  }
`;

const LoadingIcon = styled.img`
  width: 150px;
  height: 150px;
`;

const FilterPanelOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9998;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  transition: opacity 0.3s ease;
`;

const FilterPanel = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 400px;
  max-width: 90vw;
  background: white;
  z-index: 9999;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  transform: translateX(${props => props.$isOpen ? '0' : '100%'});
  transition: transform 0.3s ease;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const FilterPanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #ddd;
  
  h2 {
    margin: 0;
    font-size: 1.5rem;
    color: #333;
  }
`;

const CloseFilterButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 5px;
  
  &:hover {
    color: #333;
  }
`;

const FilterPanelContent = styled.div`
  padding: 20px;
  flex: 1;
`;

const FilterGroup = styled.div`
  margin-bottom: 30px;
  
  h3 {
    font-size: 1.1rem;
    color: #333;
    margin-bottom: 15px;
    font-weight: 600;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 0.95rem;
  color: #666;
  
  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
  
  &:hover {
    color: #333;
  }
`;

const PriceRangeGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PriceInput = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 0.95rem;
  
  &:focus {
    outline: none;
    border-color: #1a1a1a;
  }
`;

const ComboBox = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  font-size: 0.95rem;
  color: #333;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #1a1a1a;
    box-shadow: 0 0 0 3px rgba(26, 26, 26, 0.1);
  }
  
  &:hover {
    border-color: #1a1a1a;
  }
  
  option {
    padding: 8px;
  }
`;

const FilterPanelActions = styled.div`
  padding: 20px;
  border-top: 1px solid #ddd;
  display: flex;
  gap: 10px;
`;

const FilterButton = styled.button<{ $primary?: boolean }>`
  flex: 1;
  padding: 12px;
  border: 1px solid ${props => props.$primary ? '#1a1a1a' : '#ddd'};
  border-radius: 5px;
  background: ${props => props.$primary ? '#1a1a1a' : 'white'};
  color: ${props => props.$primary ? 'white' : '#333'};
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.$primary ? '#6d3410' : '#f5f5f5'};
  }
`;

const WeddingDressesPage: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('newest');
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [filters, setFilters] = useState({
    subcategories: [] as string[],
    colors: [] as string[],
    materials: [] as string[],
    sizes: [] as string[],
    priceRange: { min: 0, max: 100000 },
    inStock: false,
    onSale: false,
    // Gelinlik özel filtreler
    modelType: '',
    necklineType: '',
    hasVeil: false,
    backStyle: '',
    trainLength: ''
  });

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, sortBy, filters]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.filter-section')) {
        setSortDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const applyFiltersAndSort = () => {
    let filtered = [...products];

    // Apply advanced filters
    if (filters.subcategories.length > 0) {
      filtered = filtered.filter(product => 
        product.subcategory && filters.subcategories.includes(product.subcategory)
      );
    }

    if (filters.colors.length > 0) {
      filtered = filtered.filter(product => 
        product.colors.some(color => filters.colors.includes(color.toLowerCase()))
      );
    }

    if (filters.materials.length > 0) {
      filtered = filtered.filter(product => 
        product.materials.some(material => filters.materials.includes(material.toLowerCase()))
      );
    }

    if (filters.priceRange.min > 0 || filters.priceRange.max < 100000) {
      filtered = filtered.filter(product => 
        product.price >= filters.priceRange.min && product.price <= filters.priceRange.max
      );
    }

    if (filters.inStock) {
      filtered = filtered.filter(product => product.stock > 0);
    }

    if (filters.onSale) {
      filtered = filtered.filter(product => product.isOnSale);
    }

    // Gelinlik özel filtreler
    if (filters.modelType) {
      filtered = filtered.filter(product => 
        product.weddingDressDetails?.modelType === filters.modelType
      );
    }

    if (filters.necklineType) {
      filtered = filtered.filter(product => 
        product.weddingDressDetails?.necklineType === filters.necklineType
      );
    }

    if (filters.hasVeil) {
      filtered = filtered.filter(product => 
        product.weddingDressDetails?.veilCompatible === true
      );
    }

    if (filters.backStyle) {
      filtered = filtered.filter(product => 
        product.weddingDressDetails?.backStyle === filters.backStyle
      );
    }

    if (filters.trainLength) {
      filtered = filtered.filter(product => 
        product.weddingDressDetails?.trainLength === filters.trainLength
      );
    }

    // Sıralama
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return b.createdAt.getTime() - a.createdAt.getTime();
        case 'oldest':
          return a.createdAt.getTime() - b.createdAt.getTime();
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

    setFilteredProducts(filtered);
  };

  const loadProducts = async () => {
    setLoading(true);
    const result = await ProductsService.getProductsByCategory('gelinlik');
    if (result.success && result.data) {
      setProducts(result.data);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingState>
          <LoadingIcon src="/wedding-dress.gif" alt="Loading..." />
          <p>Gelinlikler yükleniyor...</p>
        </LoadingState>
      </PageContainer>
    );
  }

  if (filteredProducts.length === 0 && !loading) {
    return (
      <PageContainer>
        <Container>
          <SectionHeader>
            <h1>Gelinlik Koleksiyonu</h1>
          </SectionHeader>
          <HeaderLine />

          <FilterBox>
            <FilterSection className="filter-section" onClick={() => setSortDropdownOpen(!sortDropdownOpen)}>
              <FilterLabel>Sırala</FilterLabel>
              <DropdownIcon>▼</DropdownIcon>
              <FilterDropdown>
                <DropdownMenu $isOpen={sortDropdownOpen}>
                  <DropdownItem onClick={() => { setSortBy('newest'); setSortDropdownOpen(false); }}>En Yeni</DropdownItem>
                  <DropdownItem onClick={() => { setSortBy('oldest'); setSortDropdownOpen(false); }}>En Eski</DropdownItem>
                  <DropdownItem onClick={() => { setSortBy('price-low'); setSortDropdownOpen(false); }}>Fiyat (Düşük-Yüksek)</DropdownItem>
                  <DropdownItem onClick={() => { setSortBy('price-high'); setSortDropdownOpen(false); }}>Fiyat (Yüksek-Düşük)</DropdownItem>
                  <DropdownItem onClick={() => { setSortBy('name'); setSortDropdownOpen(false); }}>İsme Göre</DropdownItem>
                </DropdownMenu>
              </FilterDropdown>
            </FilterSection>
            
            <FilterSection className="filter-section" onClick={() => setFilterPanelOpen(true)}>
              <FilterLabel>Filtrele</FilterLabel>
            </FilterSection>
          </FilterBox>

          <EmptyState>
            <h2>Henüz Gelinlik Yok</h2>
            <p>
              Gelinlik koleksiyonumuz henüz hazırlanmamıştır. Yakında en güzel gelinlik tasarımlarımızla 
              sizlerle buluşacağız. Lütfen takipte kalın.
            </p>
          </EmptyState>

          {/* Filter Panel */}
          <FilterPanelOverlay $isOpen={filterPanelOpen} onClick={() => setFilterPanelOpen(false)} />
          <FilterPanel $isOpen={filterPanelOpen}>
            <FilterPanelHeader>
              <h2>Filtrele</h2>
              <CloseFilterButton onClick={() => setFilterPanelOpen(false)}>✕</CloseFilterButton>
            </FilterPanelHeader>
            
            <FilterPanelContent>
              {/* Gelinlik Özel Filtreler */}
              <FilterGroup>
                <h3>Model Tipi</h3>
                <ComboBox 
                  value={filters.modelType}
                  onChange={(e) => setFilters({ ...filters, modelType: e.target.value })}
                >
                  <option value="">Tümü</option>
                  {weddingDressOptions.modelTypes.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </ComboBox>
              </FilterGroup>

              <FilterGroup>
                <h3>Yaka Tipi</h3>
                <ComboBox 
                  value={filters.necklineType}
                  onChange={(e) => setFilters({ ...filters, necklineType: e.target.value })}
                >
                  <option value="">Tümü</option>
                  {weddingDressOptions.necklineTypes.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </ComboBox>
              </FilterGroup>

              <FilterGroup>
                <h3>Arka Kısım</h3>
                <ComboBox 
                  value={filters.backStyle}
                  onChange={(e) => setFilters({ ...filters, backStyle: e.target.value })}
                >
                  <option value="">Tümü</option>
                  {weddingDressOptions.backStyles.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </ComboBox>
              </FilterGroup>

              <FilterGroup>
                <h3>Kuyruk Uzunluğu</h3>
                <ComboBox 
                  value={filters.trainLength}
                  onChange={(e) => setFilters({ ...filters, trainLength: e.target.value })}
                >
                  <option value="">Tümü</option>
                  {weddingDressOptions.trainLengths.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </ComboBox>
              </FilterGroup>

              {/* Ortak Filtreler */}
              <FilterGroup>
                <h3>Beden</h3>
                <ComboBox 
                  value={filters.sizes[0] || ''}
                  onChange={(e) => setFilters({ ...filters, sizes: e.target.value ? [e.target.value] : [] })}
                >
                  <option value="">Tümü</option>
                  {sizeOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </ComboBox>
              </FilterGroup>

              <FilterGroup>
                <h3>Fiyat Aralığı</h3>
                <PriceRangeGroup>
                  <PriceInput 
                    type="number" 
                    placeholder="Min Fiyat" 
                    value={filters.priceRange.min || ''}
                    onChange={(e) => setFilters({ ...filters, priceRange: { ...filters.priceRange, min: parseInt(e.target.value) || 0 } })}
                  />
                  <PriceInput 
                    type="number" 
                    placeholder="Max Fiyat" 
                    value={filters.priceRange.max || ''}
                    onChange={(e) => setFilters({ ...filters, priceRange: { ...filters.priceRange, max: parseInt(e.target.value) || 100000 } })}
                  />
                </PriceRangeGroup>
              </FilterGroup>

              <FilterGroup>
                <CheckboxGroup>
                  <CheckboxLabel>
                    <input 
                      type="checkbox" 
                      checked={filters.hasVeil}
                      onChange={(e) => setFilters({ ...filters, hasVeil: e.target.checked })}
                    />
                    <span>Duvak Uygun Mu?</span>
                  </CheckboxLabel>
                  <CheckboxLabel>
                    <input 
                      type="checkbox" 
                      checked={filters.inStock}
                      onChange={(e) => setFilters({ ...filters, inStock: e.target.checked })}
                    />
                    <span>Sadece Stokta Olanlar</span>
                  </CheckboxLabel>
                  <CheckboxLabel>
                    <input 
                      type="checkbox" 
                      checked={filters.onSale}
                      onChange={(e) => setFilters({ ...filters, onSale: e.target.checked })}
                    />
                    <span>İndirimde Olanlar</span>
                  </CheckboxLabel>
                </CheckboxGroup>
              </FilterGroup>
            </FilterPanelContent>

            <FilterPanelActions>
              <FilterButton onClick={() => {
                setFilters({
                  subcategories: [],
                  colors: [],
                  materials: [],
                  sizes: [],
                  priceRange: { min: 0, max: 100000 },
                  inStock: false,
                  onSale: false,
                  modelType: '',
                  necklineType: '',
                  hasVeil: false,
                  backStyle: '',
                  trainLength: ''
                });
              }}>
                Temizle
              </FilterButton>
              <FilterButton $primary onClick={() => setFilterPanelOpen(false)}>
                Uygula
              </FilterButton>
            </FilterPanelActions>
          </FilterPanel>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container>
        <SectionHeader>
          <h1>Gelinlik Koleksiyonu</h1>
        </SectionHeader>

        <HeaderLine />

        <FilterBox>
          <FilterSection className="filter-section" onClick={() => setSortDropdownOpen(!sortDropdownOpen)}>
            <FilterLabel>Sırala</FilterLabel>
            <DropdownIcon>▼</DropdownIcon>
            <FilterDropdown>
              <DropdownMenu $isOpen={sortDropdownOpen}>
                <DropdownItem onClick={() => { setSortBy('newest'); setSortDropdownOpen(false); }}>En Yeni</DropdownItem>
                <DropdownItem onClick={() => { setSortBy('oldest'); setSortDropdownOpen(false); }}>En Eski</DropdownItem>
                <DropdownItem onClick={() => { setSortBy('price-low'); setSortDropdownOpen(false); }}>Fiyat (Düşük-Yüksek)</DropdownItem>
                <DropdownItem onClick={() => { setSortBy('price-high'); setSortDropdownOpen(false); }}>Fiyat (Yüksek-Düşük)</DropdownItem>
                <DropdownItem onClick={() => { setSortBy('name'); setSortDropdownOpen(false); }}>İsme Göre</DropdownItem>
              </DropdownMenu>
            </FilterDropdown>
          </FilterSection>
          
          <FilterSection className="filter-section" onClick={() => setFilterPanelOpen(true)}>
            <FilterLabel>Filtrele</FilterLabel>
          </FilterSection>
        </FilterBox>

        <ProductsGrid>
          {filteredProducts.map(product => (
            <ProductCard key={product.id} onClick={() => {
              const categoryMap: Record<string, string> = {
                'gelinlik': 'gelinlikler',
                'abiye': 'abiyeler',
                'kinalik': 'kinaliklar',
                'aksesuar': 'aksesuar'
              };
              const encodedName = encodeURIComponent(product.name);
              navigate(`/${categoryMap[product.category]}/${encodedName}`);
            }}>
              <ProductImageContainer>
                <ProductImageWrapper>
                  <OptimizedImage
                    src={product.images[0] || '/placeholder.webp'}
                    alt={product.name}
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    quality={75}
                    loading="lazy"
                    objectFit="cover"
                  />
                  {product.images[1] && (
                    <HoverImage
                      src={product.images[1]}
                      alt={`${product.name} - Hover`}
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      loading="lazy"
                    />
                  )}
                  <GradientOverlay />
                </ProductImageWrapper>
                <ProductBadges>
                  {product.stock === 0 && <Badge type="soldout">Stokta Yok</Badge>}
                  {product.isOnSale && <Badge type="sale">İndirim</Badge>}
                  {product.isFeatured && <Badge type="featured">Öne Çıkan</Badge>}
                  {new Date().getTime() - product.createdAt.getTime() < 7 * 24 * 60 * 60 * 1000 && (
                    <Badge type="new">Yeni</Badge>
                  )}
                </ProductBadges>
              </ProductImageContainer>
              
              <ProductContent>
                <ProductName>{product.name}</ProductName>
                <ProductPrice>
                  <CurrentPrice $onSale={product.isOnSale}>
                    ₺{product.price.toLocaleString()}
                  </CurrentPrice>
                </ProductPrice>
              </ProductContent>
            </ProductCard>
          ))}
        </ProductsGrid>
      </Container>

      {/* Filter Panel */}
      <FilterPanelOverlay $isOpen={filterPanelOpen} onClick={() => setFilterPanelOpen(false)} />
      <FilterPanel $isOpen={filterPanelOpen}>
        <FilterPanelHeader>
          <h2>Filtrele</h2>
          <CloseFilterButton onClick={() => setFilterPanelOpen(false)}>✕</CloseFilterButton>
        </FilterPanelHeader>
        
        <FilterPanelContent>
          {/* Gelinlik Özel Filtreler */}
          <FilterGroup>
            <h3>Model Tipi</h3>
            <ComboBox 
              value={filters.modelType}
              onChange={(e) => setFilters({ ...filters, modelType: e.target.value })}
            >
              <option value="">Tümü</option>
              {weddingDressOptions.modelTypes.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </ComboBox>
          </FilterGroup>

          <FilterGroup>
            <h3>Yaka Tipi</h3>
            <ComboBox 
              value={filters.necklineType}
              onChange={(e) => setFilters({ ...filters, necklineType: e.target.value })}
            >
              <option value="">Tümü</option>
              {weddingDressOptions.necklineTypes.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </ComboBox>
          </FilterGroup>

          <FilterGroup>
            <h3>Arka Kısım</h3>
            <ComboBox 
              value={filters.backStyle}
              onChange={(e) => setFilters({ ...filters, backStyle: e.target.value })}
            >
              <option value="">Tümü</option>
              {weddingDressOptions.backStyles.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </ComboBox>
          </FilterGroup>

          <FilterGroup>
            <h3>Kuyruk Uzunluğu</h3>
            <ComboBox 
              value={filters.trainLength}
              onChange={(e) => setFilters({ ...filters, trainLength: e.target.value })}
            >
              <option value="">Tümü</option>
              {weddingDressOptions.trainLengths.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </ComboBox>
          </FilterGroup>

          {/* Ortak Filtreler */}
          <FilterGroup>
            <h3>Beden</h3>
            <ComboBox 
              value={filters.sizes[0] || ''}
              onChange={(e) => setFilters({ ...filters, sizes: e.target.value ? [e.target.value] : [] })}
            >
              <option value="">Tümü</option>
              {sizeOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </ComboBox>
          </FilterGroup>

          <FilterGroup>
            <h3>Fiyat Aralığı</h3>
            <PriceRangeGroup>
              <PriceInput 
                type="number" 
                placeholder="Min Fiyat" 
                value={filters.priceRange.min || ''}
                onChange={(e) => setFilters({ ...filters, priceRange: { ...filters.priceRange, min: parseInt(e.target.value) || 0 } })}
              />
              <PriceInput 
                type="number" 
                placeholder="Max Fiyat" 
                value={filters.priceRange.max || ''}
                onChange={(e) => setFilters({ ...filters, priceRange: { ...filters.priceRange, max: parseInt(e.target.value) || 100000 } })}
              />
            </PriceRangeGroup>
          </FilterGroup>

          <FilterGroup>
            <CheckboxGroup>
              <CheckboxLabel>
                <input 
                  type="checkbox" 
                  checked={filters.hasVeil}
                  onChange={(e) => setFilters({ ...filters, hasVeil: e.target.checked })}
                />
                <span>Duvak Uygun Mu?</span>
              </CheckboxLabel>
              <CheckboxLabel>
                <input 
                  type="checkbox" 
                  checked={filters.inStock}
                  onChange={(e) => setFilters({ ...filters, inStock: e.target.checked })}
                />
                <span>Sadece Stokta Olanlar</span>
              </CheckboxLabel>
              <CheckboxLabel>
                <input 
                  type="checkbox" 
                  checked={filters.onSale}
                  onChange={(e) => setFilters({ ...filters, onSale: e.target.checked })}
                />
                <span>İndirimde Olanlar</span>
              </CheckboxLabel>
            </CheckboxGroup>
          </FilterGroup>
        </FilterPanelContent>

        <FilterPanelActions>
          <FilterButton onClick={() => {
            setFilters({
              subcategories: [],
              colors: [],
              materials: [],
              sizes: [],
              priceRange: { min: 0, max: 100000 },
              inStock: false,
              onSale: false,
              modelType: '',
              necklineType: '',
              hasVeil: false,
              backStyle: '',
              trainLength: ''
            });
          }}>
            Temizle
          </FilterButton>
          <FilterButton $primary onClick={() => setFilterPanelOpen(false)}>
            Uygula
          </FilterButton>
        </FilterPanelActions>
      </FilterPanel>
    </PageContainer>
  );
};

export default WeddingDressesPage;
